import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { c as cosmosTokens } from './cosmos-tokens.js';

const debounce = (fn, timeout = 300) => {
  let timer = null;
  return (...args) => {
    const next = () => fn(...args);
    if (timer)
      clearTimeout(timer);
    timer = setTimeout(next, timeout);
  };
};

const styles = "/* Easing and Timing */\n/* Grid */\n/* Component */\n/* 1px */\n/* Breakpoints */\n/* Structure */\n/* Modal */\n/* Drawer */\n/* Colors */\n:host {\n  display: var(--hc-tooltip-host-display, block) !important;\n  position: var(--hc-tooltip-host-position, absolute) !important;\n  z-index: var(--hc-tooltip-host-z-index, 1060) !important;\n}\n\n.hc-tooltip-concrete {\n  background-color: var(--hc-tooltip-background-color, var(--hc-color-neutral-900));\n  border-radius: var(--hc-tooltip-border-radius, var(--hc-size-border-radius-medium));\n  box-shadow: var(--hc-tooltip-box-shadow, var(--hc-shadow-medium));\n  box-sizing: var(--hc-tooltip-box-sizing, border-box);\n  color: var(--hc-tooltip-color, var(--hc-color-neutral-0));\n  display: var(--hc-tooltip-display, block);\n  font-family: var(--hc-tooltip-font-family, var(--hc-font-family-primary));\n  font-size: var(--hc-tooltip-font-size, var(--hc-size-text-small));\n  font-weight: var(--hc-tooltip-font-weight, var(--hc-font-weight-bold));\n  letter-spacing: var(--hc-tooltip-letter-spacing, var(--hc-font-letter-spacing-wide));\n  line-break: var(--hc-tooltip-line-break, auto);\n  line-height: var(--hc-tooltip-line-height, var(--hc-font-line-height-large));\n  max-width: var(--hc-tooltip-max-width, 225px);\n  opacity: var(--hc-tooltip-opacity, 0);\n  padding: var(--hc-tooltip-padding, var(--hc-size-spacing-2) var(--hc-size-spacing-4));\n  position: var(--hc-tooltip-position, relative);\n  transition: var(--hc-tooltip-transition, opacity var(--hc-timing, 500ms) var(--hc-easing, cubic-bezier(0.2, 1, 0.2, 1)));\n}\n.hc-tooltip-concrete--show {\n  opacity: var(--hc-tooltip-show-opacity, 1);\n}\n.hc-tooltip-concrete__arrow::before {\n  background-color: var(--hc-tooltip-arrow-before-background-color, var(--hc-color-neutral-900));\n  content: var(--hc-tooltip-arrow-before-content, \"\");\n  display: var(--hc-tooltip-arrow-before-display, block);\n  height: var(--hc-tooltip-arrow-before-height, var(--hc-size-spacing-3));\n  position: var(--hc-tooltip-arrow-before-position, absolute);\n  width: var(--hc-tooltip-arrow-before-width, var(--hc-size-spacing-3));\n}\n.hc-tooltip-concrete__arrow--bottom {\n  margin-top: var(--hc-tooltip-arrow-bottom-margin-top, var(--hc-size-spacing-3));\n}\n.hc-tooltip-concrete__arrow--bottom::before {\n  left: var(--hc-tooltip-arrow-bottom-before-left, 50%);\n  top: var(--hc-tooltip-arrow-bottom-before-top, calc(var(--hc-size-spacing-1) * -1.5));\n  transform: var(--hc-tooltip-arrow-bottom-before-transform, translateX(-50%) rotate(45deg));\n}\n.hc-tooltip-concrete__arrow--end {\n  margin-inline-start: var(--hc-tooltip-arrow-end-margin-inline-start, var(--hc-size-spacing-3));\n}\n.hc-tooltip-concrete__arrow--end::before {\n  inset-inline-start: var(--hc-tooltip-arrow-end-before-inset-inline-start, calc(var(--hc-size-spacing-1) * -1.5));\n  top: var(--hc-tooltip-arrow-end-before-top, 50%);\n  transform: var(--hc-tooltip-arrow-end-before-transform, translateY(-50%) rotate(45deg));\n}\n.hc-tooltip-concrete__arrow--start {\n  margin-inline-end: var(--hc-tooltip-arrow-start-margin-inline-end, var(--hc-size-spacing-3));\n}\n.hc-tooltip-concrete__arrow--start::before {\n  inset-inline-end: var(--hc-tooltip-arrow-start-before-inset-inline-end, calc(var(--hc-size-spacing-1) * -1.5));\n  top: var(--hc-tooltip-arrow-start-before-top, 50%);\n  transform: var(--hc-tooltip-arrow-start-before-transform, translateY(-50%) rotate(45deg));\n}\n.hc-tooltip-concrete__arrow--top {\n  margin-bottom: var(--hc-tooltip-arrow-top-margin-bottom, var(--hc-size-spacing-3));\n}\n.hc-tooltip-concrete__arrow--top::before {\n  bottom: var(--hc-tooltip-arrow-top-before-bottom, calc(var(--hc-size-spacing-1) * -1.5));\n  left: var(--hc-tooltip-arrow-top-before-left, 50%);\n  transform: var(--hc-tooltip-arrow-top-before-transform, translateX(-50%) rotate(45deg));\n}";

const HcTooltipConcrete$1 = /*@__PURE__*/ proxyCustomElement(class HcTooltipConcrete extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.__attachShadow();
    this.hcTooltipConcreteLoaded = createEvent(this, "hcTooltipConcreteLoaded", 7);
    this.debounceHide = null;
    this.position = 'top';
    this.role = 'tooltip';
    this.text = undefined;
    this.toggleVisibility = false;
  }
  connectedCallback() {
    this.debounceHide = debounce(() => {
      this.container.addEventListener('transitionend', this.removeTooltip.bind(this));
      this.toggleVisibility = false;
    }, 100);
  }
  componentDidLoad() {
    this.container = this.el.shadowRoot.querySelector('.hc-tooltip-concrete');
    this.hcTooltipConcreteLoaded.emit();
  }
  /**
   * Call this method to show the tooltip
   */
  async show(boundingClientRect, isRTL) {
    this.toggleVisibility = true;
    this.updatePlacement(boundingClientRect, isRTL);
  }
  /**
   * Call this method to hide the tooltip
   */
  async hide() {
    this.debounceHide();
  }
  removeTooltip() {
    this.container.removeEventListener('transitionend', this.removeTooltip);
    this.el.remove();
  }
  async updatePlacement({ height, left, top, width }, isRTL) {
    const { offsetHeight, offsetWidth } = this.el;
    const { scrollY } = window;
    let start = left - offsetWidth;
    let end = left + width;
    const horizontalPosition = `${(left + (width / 2)) - offsetWidth / 2}px`;
    const topPosition = `${(top + scrollY + (height / 2)) - offsetHeight / 2}px`;
    if (isRTL) {
      start = left + width;
      end = left - offsetWidth;
    }
    if (this.position === 'start') {
      this.el.style.top = topPosition;
      return this.el.style.left = `${start}px`;
    }
    if (this.position === 'end') {
      this.el.style.top = topPosition;
      return this.el.style.left = `${end}px`;
    }
    if (this.position === 'top') {
      this.el.style.top = `${(top + scrollY) - offsetHeight}px`;
      return this.el.style.left = horizontalPosition;
    }
    if (this.position === 'bottom') {
      this.el.style.top = `${top + scrollY + height}px`;
      return this.el.style.left = horizontalPosition;
    }
  }
  getCSSClass() {
    return {
      'hc-tooltip-concrete': true,
      'hc-tooltip-concrete__arrow': true,
      [`hc-tooltip-concrete__arrow--${this.position}`]: Boolean(this.position),
      'hc-tooltip-concrete--show': this.toggleVisibility
    };
  }
  render() {
    return (h(Host, null, h("div", { class: this.getCSSClass() }, this.text), h("style", { type: "text/css" }, styles.toString(), cosmosTokens.getStyle())));
  }
  get el() { return this; }
}, [1, "hc-tooltip-concrete", {
    "position": [1],
    "role": [513],
    "text": [1],
    "toggleVisibility": [32],
    "show": [64],
    "hide": [64]
  }, [[9, "scroll", "updatePlacement"]]]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["hc-tooltip-concrete"];
  components.forEach(tagName => { switch (tagName) {
    case "hc-tooltip-concrete":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, HcTooltipConcrete$1);
      }
      break;
  } });
}

const HcTooltipConcrete = HcTooltipConcrete$1;
const defineCustomElement = defineCustomElement$1;

export { HcTooltipConcrete, defineCustomElement };

//# sourceMappingURL=hc-tooltip-concrete.js.map